// =============================================================================
// EXPENSE POLICY RULE ENGINE
// Wrapper around rulesEngine.ts for expense policy enforcement
// =============================================================================

import { evaluateRules } from '@/components/forms/engine/rulesEngine';
import type { RuleDef } from '@/components/forms/engine/rulesEngine';
import {
  findApplicableExpensePolicies,
  aggregateExpensePolicyResults,
  expensePolicyMatches,
} from './policyRules';
import type {
  ExpensePolicyRule,
  ExpenseData,
  ExpenseLineItemData,
  ExpensePolicyEvaluationResult,
  ExpensePolicyMatch,
} from './policyRules';

// Re-export for convenience
export type { ExpensePolicyRule, ExpenseData, ExpenseLineItemData, ExpensePolicyEvaluationResult, ExpensePolicyMatch };

// ============================================================================
// CONVERSION: ExpensePolicyRule -> RuleDef
// =============================================================================

/**
 * Convert an ExpensePolicyRule into one or more RuleDefs for the rulesEngine.
 *
 * "all" conditions are ANDed within a single RuleDef (rulesEngine conditions array).
 * "any" conditions are OR semantics — each becomes its own RuleDef so that if
 *   any one fires, the action is triggered.
 *
 * Returns an array: typically one RuleDef (all conditions + first any), plus
 * one additional RuleDef per remaining "any" condition.
 */
function convertExpensePolicyToRuleDefs(policy: ExpensePolicyRule): RuleDef[] {
  const conditions = policy.conditions || {};

  const actionMap: Record<string, RuleDef['action']> = {
    block_submission: 'block_submit',
    require_approval: 'warn',
    flag_for_review: 'flag',
    auto_categorize: 'set_value',
    warn_user: 'warn',
    auto_approve: 'warn',
  };

  const baseAction = actionMap[policy.action_type] || 'warn';
  const baseRuleDef: Omit<RuleDef, 'id' | 'conditions'> = {
    label: policy.rule_name,
    trigger_field: 'amount',
    condition: undefined,
    action: baseAction,
    message: policy.action_config.message,
    target_field: policy.action_config.category_id ? 'category_id' : undefined,
    set_value: policy.action_config.category_id,
    severity: policy.action_config.severity,
    priority: policy.priority,
  };

  // Build the "all" conditions array (AND logic within a single rule)
  const allConditions: RuleDef['conditions'] = [];
  if (conditions.all && conditions.all.length > 0) {
    for (const cond of conditions.all) {
      const value = Array.isArray(cond.value) ? cond.value.join(',') : cond.value;
      allConditions.push({
        field: cond.field,
        operator: cond.operator as any,
        value: value as any,
      });
    }
  }

  const anyConds = conditions.any || [];

  if (anyConds.length === 0) {
    // No "any" conditions — single rule with only "all" conditions
    return [{
      ...baseRuleDef,
      id: policy.id,
      conditions: allConditions.length > 0 ? allConditions : undefined,
    }];
  }

  // Each "any" condition becomes its own RuleDef (OR semantics).
  // All RuleDefs share the same "all" conditions (AND base) + one "any" condition.
  return anyConds.map((cond, idx) => {
    const value = Array.isArray(cond.value) ? cond.value.join(',') : cond.value;
    const ruleConditions = [...allConditions, {
      field: cond.field,
      operator: cond.operator as any,
      value: value as any,
    }];
    return {
      ...baseRuleDef,
      id: idx === 0 ? policy.id : `${policy.id}__any_${idx}`,
      conditions: ruleConditions.length > 0 ? ruleConditions : undefined,
    };
  });
}

// ============================================================================
// POLICY ENGINE - Main Evaluation Function
// =============================================================================

export interface ExpensePolicyEngineOptions {
  clientId: number;
  userId?: number;
  userRole?: string;
}

/**
 * Evaluate expense policies against expense data.
 * 
 * @param policies - Array of expense policy rules from database
 * @param expenseData - Expense data to evaluate
 * @param options - Engine options (client_id, user context)
 * @returns Aggregated policy evaluation result
 */
export function evaluateExpensePolicies(
  policies: ExpensePolicyRule[],
  expenseData: ExpenseData,
  options: ExpensePolicyEngineOptions
): ExpensePolicyEvaluationResult {
  // Filter policies by client, active status, and user/role targeting
  const applicablePolicies = policies.filter(
    (p) => {
      if (p.client_id !== options.clientId || !p.is_active) return false;
      // If applies_to_all is true or undefined (default), policy applies to everyone
      if (p.applies_to_all !== false) return true;
      // Check role targeting
      if (p.target_roles && p.target_roles.length > 0) {
        if (options.userRole && p.target_roles.includes(options.userRole)) return true;
      }
      // Check user targeting
      if (p.target_user_ids && p.target_user_ids.length > 0) {
        if (options.userId && p.target_user_ids.includes(options.userId)) return true;
      }
      // If neither roles nor users are specified but applies_to_all is false, don't apply
      const hasRoleTarget = p.target_roles && p.target_roles.length > 0;
      const hasUserTarget = p.target_user_ids && p.target_user_ids.length > 0;
      if (!hasRoleTarget && !hasUserTarget) return true; // Fallback: no targeting configured = apply to all
      return false;
    }
  );

  if (applicablePolicies.length === 0) {
    return {
      matchedPolicies: [],
      hasBlockers: false,
      requiresApproval: false,
      requiredApprovalLevel: 0,
      blockerMessages: [],
      warningMessages: [],
      flaggedFields: new Map(),
      autoCategorize: null,
      requireVendorRegistration: null,
      autoApproval: null,
      firedRules: [],
      hasBlockersFromRules: false,
      blockerMessagesFromRules: [],
      warningMessagesFromRules: [],
      flaggedFieldsFromRules: new Map(),
      valueOverrides: {},
      dynamicRequired: new Set(),
      dynamicUnrequired: new Set(),
      hiddenSections: new Set(),
      shownSections: new Set(),
      hiddenFields: new Set(),
      shownFields: new Set(),
      jumpTarget: null,
      captchaRequired: false,
      scoreOverrides: {},
    };
  }

  // Convert policies to RuleDef format for rulesEngine
  const ruleDefs: RuleDef[] = applicablePolicies.flatMap(convertExpensePolicyToRuleDefs);

  // Evaluate using rulesEngine
  const rulesResult = evaluateRules(ruleDefs, expenseData);

  // Find direct policy matches for expense-specific logic
  const matches = findApplicableExpensePolicies(applicablePolicies, expenseData);
  const policyResult = aggregateExpensePolicyResults(matches);

  // Merge results from both engines
  return {
    ...policyResult,
    ...rulesResult,
    // Use policy-specific results as primary
    hasBlockers: policyResult.hasBlockers,
    requiresApproval: policyResult.requiresApproval,
    requiredApprovalLevel: policyResult.requiredApprovalLevel,
    autoCategorize: policyResult.autoCategorize,
    // Sync rulesEngine result fields
    hasBlockersFromRules: policyResult.hasBlockers,
    blockerMessagesFromRules: policyResult.blockerMessages,
    warningMessagesFromRules: policyResult.warningMessages,
    flaggedFieldsFromRules: policyResult.flaggedFields,
  };
}

// ============================================================================
// POLICY VALIDATION - Check if expense can be submitted
// =============================================================================

export interface ExpenseValidationResult {
  canSubmit: boolean;
  requiresApproval: boolean;
  approvalLevel: number;
  blockers: string[];
  warnings: string[];
  autoCategorize: { category_id?: number; category_name?: string } | null;
  appliedPolicies: string[];
}

/**
 * Validate expense against policies before submission.
 * 
 * @param policies - Array of expense policy rules
 * @param expenseData - Expense data to validate
 * @param options - Engine options
 * @returns Validation result with submit decision
 */
export function validateExpenseSubmission(
  policies: ExpensePolicyRule[],
  expenseData: ExpenseData,
  options: ExpensePolicyEngineOptions
): ExpenseValidationResult {
  const result = evaluateExpensePolicies(policies, expenseData, options);

  return {
    canSubmit: !result.hasBlockers,
    requiresApproval: result.requiresApproval,
    approvalLevel: result.requiredApprovalLevel,
    blockers: result.blockerMessages,
    warnings: result.warningMessages,
    autoCategorize: result.autoCategorize,
    appliedPolicies: result.matchedPolicies.map((m) => m.rule.rule_name),
  };
}

// ============================================================================
// POLICY APPLICATION - Track policy usage
// =============================================================================

/**
 * Mark a policy as applied (for analytics/auditing).
 * This should be called after a policy action is taken.
 * 
 * @param policyId - ID of the policy to mark as applied
 * @returns Updated policy object (for database update)
 */
export function markPolicyApplied(policyId: string): Partial<ExpensePolicyRule> {
  return {
    id: policyId,
    times_applied: undefined, // Will be incremented in database
    last_applied_at: new Date().toISOString(),
  };
}

// ============================================================================
// POLICY SIMULATION - Test policies without applying
// =============================================================================

export interface PolicySimulationResult {
  policyName: string;
  wouldMatch: boolean;
  action: string;
  message?: string;
  matchedConditions: number;
}

/**
 * Simulate policy evaluation for testing/admin UI.
 * 
 * @param policies - Array of expense policy rules
 * @param expenseData - Expense data to test
 * @param options - Engine options
 * @returns Array of simulation results for each policy
 */
export function simulateExpensePolicies(
  policies: ExpensePolicyRule[],
  expenseData: ExpenseData,
  options: ExpensePolicyEngineOptions
): PolicySimulationResult[] {
  const applicablePolicies = policies.filter(
    (p) => p.client_id === options.clientId
  );

  return applicablePolicies.map((policy) => {
    const match = expensePolicyMatches(policy, expenseData);

    return {
      policyName: policy.rule_name,
      wouldMatch: match !== null,
      action: policy.action_type,
      message: policy.action_config.message,
      matchedConditions: match?.matched_conditions || 0,
    };
  });
}
